import { z } from "zod";
import type { AppVersion, AppVersionInput } from "@/api/appVersions.api";

function isUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

/** Empty is allowed (a store link can be added later); anything else must be a real URL. */
const storeUrl = (message: string) =>
  z
    .string()
    .max(500, "Too long")
    .refine((v) => v === "" || isUrl(v), message);

export const policyFormSchema = z.object({
  latestBuildNumber: z.coerce
    .number({ invalid_type_error: "Enter the version number" })
    .int("Whole numbers only")
    .min(1, "Must be at least 1"),
  forceUpdate: z.boolean(),
  releaseNotes: z.string().max(1000, "Max 1000 characters"),
  androidStoreUrl: storeUrl("Enter a valid Google Play URL"),
  iosStoreUrl: storeUrl("Enter a valid App Store URL"),
});

export type PolicyFormValues = z.infer<typeof policyFormSchema>;

export function toPolicyFormValues(current: AppVersion | undefined): PolicyFormValues {
  return {
    // An app that was never set has no version yet — leave the field empty rather than showing 0.
    latestBuildNumber: (current?.latestBuildNumber ?? undefined) as unknown as number,
    forceUpdate: current?.forceUpdate ?? false,
    releaseNotes: current?.releaseNotes ?? "",
    androidStoreUrl: current?.androidStoreUrl ?? "",
    iosStoreUrl: current?.iosStoreUrl ?? "",
  };
}

/** The body for PUT /admin/app-versions/:app. */
export function buildUpdatePayload(values: PolicyFormValues): AppVersionInput {
  return {
    latestBuildNumber: values.latestBuildNumber,
    forceUpdate: values.forceUpdate,
    releaseNotes: values.releaseNotes,
    androidStoreUrl: values.androidStoreUrl,
    iosStoreUrl: values.iosStoreUrl,
  };
}
